// ===== MessageReadReceipts: unread recipient count UI =====
// Peer human messages produce read receipts when visible. Self-authored room messages render the
// remaining unread-recipient count from the server's send-time recipient snapshot.

type MrCountsResponse = { messages?: Array<{ seq?: unknown; count?: unknown }>; counts?: Array<{ seq?: unknown; count?: unknown }> };
type MrReadState = { count: number; stamp: number };
type MrBufferedCount = { sessionId: string; seq: number; count: number; stamp: number; expiresAt: number };
type MrPendingEntry = { sessionId: string; seq: number; attempts: number; timerId: number | null; inFlight: boolean };

var MessageReadReceipts = (function () {
  "use strict";

  var RANGE_CHUNK = 200;
  var BUFFER_LIMIT = 200;
  var BUFFER_TTL_MS = 5 * 60 * 1000;
  var RETRY_DELAYS_MS = [1000, 2000, 4000];

  var readState = new Map<string, MrReadState>();
  var pending = new Map<string, MrPendingEntry>();
  var delivered = new Set<string>();
  var visibleKeys = new Set<string>();
  var bufferedCounts = new Map<string, MrBufferedCount>();
  var observer: IntersectionObserver | null = null;
  var liveStamp = 0;

  function _isRecord(value: unknown): value is Record<string, unknown> {
    return typeof value === "object" && value !== null && !Array.isArray(value);
  }

  function _key(sessionId: string, seq: number): string {
    return sessionId + "#" + seq;
  }

  function _statusCodeOf(error: unknown): number | undefined {
    return typeof error === "object" && error !== null && "statusCode" in error
      ? Number(Reflect.get(error, "statusCode"))
      : undefined;
  }

  async function _fetchJson(url: string, options?: RequestInit): Promise<Record<string, unknown>> {
    var response = await fetch(url, options);
    var payload: unknown = await response.json().catch(function () { return {}; });
    if (!_isRecord(payload)) throw new Error("Invalid JSON response");
    if (!response.ok || payload.ok === false) {
      var requestError = new Error(typeof payload.error === "string" ? payload.error : "Request failed: " + response.status) as Error & { statusCode?: number };
      requestError.statusCode = response.status;
      throw requestError;
    }
    return payload;
  }

  function _chatMessagesEl(): HTMLElement | null {
    return document.getElementById("chatMessages");
  }

  function _isReceiptEligibleMessage(messageEl: Element | null | undefined): messageEl is HTMLElement {
    return messageEl instanceof HTMLElement
      && messageEl.dataset.readReceiptEligible === "true"
      && typeof messageEl.dataset.sessionId === "string"
      && Number.isInteger(Number(messageEl.dataset.msgSeq));
  }

  function _isUnreadCountEligibleMessage(messageEl: Element | null | undefined): messageEl is HTMLElement {
    return messageEl instanceof HTMLElement
      && messageEl.dataset.unreadCountEligible === "true"
      && typeof messageEl.dataset.sessionId === "string"
      && Number.isInteger(Number(messageEl.dataset.msgSeq));
  }

  function _messageSessionId(messageEl: HTMLElement): string {
    return messageEl.dataset.sessionId || "";
  }

  function _messageSeq(messageEl: HTMLElement): number {
    return Number(messageEl.dataset.msgSeq || -1);
  }

  function _findMessageEl(sessionId: string, seq: number): HTMLElement | null {
    var container = _chatMessagesEl();
    if (!container) return null;
    var nodes = container.querySelectorAll<HTMLElement>('[data-msg-seq="' + seq + '"][data-unread-count-eligible="true"]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].dataset.sessionId === sessionId) return nodes[i];
    }
    return null;
  }

  function _findReceiptMessageEl(sessionId: string, seq: number): HTMLElement | null {
    var container = _chatMessagesEl();
    if (!container) return null;
    var nodes = container.querySelectorAll<HTMLElement>('[data-msg-seq="' + seq + '"][data-read-receipt-eligible="true"]');
    for (var i = 0; i < nodes.length; i++) {
      if (nodes[i].dataset.sessionId === sessionId) return nodes[i];
    }
    return null;
  }

  // 안 읽은 사람 수는 보낸 시각과 같은 줄이 아니라 말풍선 바깥쪽에 붙는다.
  // .bubble-row는 이미 flex이고 `.message.user .bubble-row`가 row-reverse라,
  // 말풍선 뒤에 넣기만 하면 자기 메시지는 왼쪽 / 상대 메시지는 오른쪽으로 갈린다.
  // .msg-actions 앞에 넣어야 액션 거터(호버 전에도 자리를 차지한다)만큼 말풍선에서 떨어지지 않는다.
  function _countStampEl(messageEl: HTMLElement): HTMLElement | null {
    var row = messageEl.querySelector<HTMLElement>(".bubble-row");
    if (!row) return null;
    var stamp = row.querySelector<HTMLElement>(".message-read-count");
    if (stamp) return stamp;
    stamp = document.createElement("span");
    stamp.className = "message-read-count";
    row.insertBefore(stamp, row.querySelector(".msg-actions"));
    return stamp;
  }

  function _renderCount(sessionId: string, seq: number): void {
    var messageEl = _findMessageEl(sessionId, seq);
    if (!messageEl) return;
    var stamp = _countStampEl(messageEl);
    if (!stamp) return;
    var state = readState.get(_key(sessionId, seq));
    if (!state || state.count <= 0) {
      stamp.remove();
      return;
    }
    stamp.textContent = String(state.count);
    stamp.setAttribute("aria-label", "안 읽은 사람 " + state.count + "명");
  }

  function _pruneBufferedCounts(now = Date.now()): void {
    bufferedCounts.forEach(function (entry, key) {
      if (entry.expiresAt <= now) bufferedCounts.delete(key);
    });
    if (bufferedCounts.size <= BUFFER_LIMIT) return;
    var oldest = Array.from(bufferedCounts.entries()).sort(function (a, b) { return a[1].stamp - b[1].stamp; });
    while (oldest.length > BUFFER_LIMIT) {
      var victim = oldest.shift();
      if (victim) bufferedCounts.delete(victim[0]);
    }
  }

  function _applyCountIfFresh(sessionId: string, seq: number, count: number, issuedAt: number): void {
    var key = _key(sessionId, seq);
    var existing = readState.get(key);
    if (existing && existing.stamp > issuedAt) return;
    readState.set(key, { count: count, stamp: issuedAt });
    _renderCount(sessionId, seq);
  }

  function _bufferCount(sessionId: string, seq: number, count: number, stamp: number): void {
    bufferedCounts.set(_key(sessionId, seq), {
      sessionId: sessionId,
      seq: seq,
      count: count,
      stamp: stamp,
      expiresAt: Date.now() + BUFFER_TTL_MS,
    });
    _pruneBufferedCounts();
  }

  function _applyCountOrBuffer(sessionId: string, seq: number, count: number, stamp: number): void {
    if (_findMessageEl(sessionId, seq)) {
      _applyCountIfFresh(sessionId, seq, count, stamp);
      bufferedCounts.delete(_key(sessionId, seq));
      return;
    }
    _bufferCount(sessionId, seq, count, stamp);
  }

  function _applyBufferedCount(messageEl: HTMLElement): void {
    var sessionId = _messageSessionId(messageEl);
    var seq = _messageSeq(messageEl);
    var buffered = bufferedCounts.get(_key(sessionId, seq));
    if (!buffered) return;
    if (buffered.expiresAt <= Date.now()) {
      bufferedCounts.delete(_key(sessionId, seq));
      return;
    }
    _applyCountIfFresh(sessionId, seq, buffered.count, buffered.stamp);
    bufferedCounts.delete(_key(sessionId, seq));
  }

  function _ensureObserver(): IntersectionObserver | null {
    if (observer) return observer;
    if (typeof IntersectionObserver !== "function") return null;
    observer = new IntersectionObserver(function (entries) {
      for (var i = 0; i < entries.length; i++) {
        var entry = entries[i];
        if (!_isReceiptEligibleMessage(entry.target)) continue;
        var key = _key(_messageSessionId(entry.target), _messageSeq(entry.target));
        var isVisible = Boolean(entry.isIntersecting || entry.intersectionRatio > 0);
        if (isVisible) visibleKeys.add(key);
        else visibleKeys.delete(key);
        if (isVisible) _queueForSend(entry.target);
      }
    }, { threshold: 0.01 });
    return observer;
  }

  function _observeMessage(messageEl: HTMLElement): void {
    var io = _ensureObserver();
    if (!io) return;
    io.observe(messageEl);
  }

  function _disconnectObserver(): void {
    if (!observer) return;
    observer.disconnect();
    observer = null;
  }

  function _isCurrentVisibleSession(sessionId: string): boolean {
    return document.visibilityState === "visible"
      && typeof window !== "undefined"
      && window.currentSessionId === sessionId;
  }

  function _canSendForMessage(messageEl: HTMLElement): boolean {
    if (!_isReceiptEligibleMessage(messageEl) || !messageEl.isConnected) return false;
    var sessionId = _messageSessionId(messageEl);
    var seq = _messageSeq(messageEl);
    if (!_isCurrentVisibleSession(sessionId)) return false;
    if (!visibleKeys.has(_key(sessionId, seq))) return false;
    return true;
  }

  function _pendingForKey(key: string, sessionId: string, seq: number): MrPendingEntry {
    var existing = pending.get(key);
    if (existing) return existing;
    var created: MrPendingEntry = { sessionId: sessionId, seq: seq, attempts: 0, timerId: null, inFlight: false };
    pending.set(key, created);
    return created;
  }

  function _clearPendingTimer(entry: MrPendingEntry): void {
    if (entry.timerId === null) return;
    window.clearTimeout(entry.timerId);
    entry.timerId = null;
  }

  function _queueForSend(messageEl: HTMLElement): void {
    if (!_isReceiptEligibleMessage(messageEl)) return;
    var sessionId = _messageSessionId(messageEl);
    var seq = _messageSeq(messageEl);
    var key = _key(sessionId, seq);
    if (delivered.has(key)) return;
    var entry = _pendingForKey(key, sessionId, seq);
    if (entry.attempts >= RETRY_DELAYS_MS.length) entry.attempts = 0;
    _clearPendingTimer(entry);
    void _flushPending(sessionId);
  }

  function _scheduleRetry(entry: MrPendingEntry): void {
    if (entry.attempts >= RETRY_DELAYS_MS.length) return;
    var delay = RETRY_DELAYS_MS[entry.attempts];
    entry.attempts += 1;
    entry.timerId = window.setTimeout(function () {
      entry.timerId = null;
      void _flushPending(entry.sessionId);
    }, delay);
  }

  async function _flushPending(sessionId: string): Promise<void> {
    if (!_isCurrentVisibleSession(sessionId)) return;
    var keys = Array.from(pending.keys()).filter(function (key) { return key.indexOf(sessionId + "#") === 0; });
    var seqs: number[] = [];
    var flushEntries: MrPendingEntry[] = [];
    for (var i = 0; i < keys.length; i++) {
      var entry = pending.get(keys[i]);
      if (!entry) continue;
      if (entry.inFlight) continue;
      var messageEl = _findReceiptMessageEl(entry.sessionId, entry.seq);
      if (!messageEl || !_canSendForMessage(messageEl)) continue;
      flushEntries.push(entry);
      seqs.push(entry.seq);
    }
    if (!seqs.length) return;

    var issuedAt = liveStamp;
    flushEntries.forEach(function (entry) {
      entry.inFlight = true;
    });
    try {
      var body = await _fetchJson("/api/rooms/sessions/" + encodeURIComponent(sessionId) + "/read-receipts", {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ seqs: seqs }),
      }) as MrCountsResponse & Record<string, unknown>;
      var counts = Array.isArray(body.counts) ? body.counts : [];
      for (var j = 0; j < flushEntries.length; j++) {
        _clearPendingTimer(flushEntries[j]);
        pending.delete(_key(flushEntries[j].sessionId, flushEntries[j].seq));
        delivered.add(_key(flushEntries[j].sessionId, flushEntries[j].seq));
      }
      counts.forEach(function (item) {
        var seq = _isRecord(item) ? Number(item.seq) : Number.NaN;
        if (!Number.isInteger(seq) || typeof item.count !== "number") return;
        _applyCountIfFresh(sessionId, seq, item.count, issuedAt);
      });
    } catch (error: unknown) {
      flushEntries.forEach(function (entry) {
        entry.inFlight = false;
      });
      var status = _statusCodeOf(error);
      if (status === 400 || status === 403 || status === 404 || status === 503) {
        flushEntries.forEach(function (entry) {
          _clearPendingTimer(entry);
        });
        return;
      }
      flushEntries.forEach(_scheduleRetry);
    }
  }

  function _splitRange(fromSeq: number, toSeq: number): Array<{ from: number; to: number }> {
    var chunks: Array<{ from: number; to: number }> = [];
    var start = fromSeq;
    while (start <= toSeq) {
      var end = Math.min(start + RANGE_CHUNK, toSeq);
      chunks.push({ from: start, to: end });
      start = end + 1;
    }
    return chunks;
  }

  async function _fetchRangeAndApply(sessionId: string, fromSeq: number, toSeq: number): Promise<void> {
    var issuedAt = liveStamp;
    var chunks = _splitRange(fromSeq, toSeq);
    for (var i = 0; i < chunks.length; i++) {
      if (typeof window !== "undefined" && window.currentSessionId !== sessionId) return;
      var body = await _fetchJson(
        "/api/rooms/sessions/" + encodeURIComponent(sessionId) + "/read-receipts?fromSeq=" + chunks[i].from + "&toSeq=" + chunks[i].to,
      ) as MrCountsResponse & Record<string, unknown>;
      if (typeof window !== "undefined" && window.currentSessionId !== sessionId) return;
      var messages = Array.isArray(body.messages) ? body.messages : [];
      messages.forEach(function (item) {
        var seq = _isRecord(item) ? Number(item.seq) : Number.NaN;
        if (!Number.isInteger(seq) || typeof item.count !== "number") return;
        _applyCountIfFresh(sessionId, seq, item.count, issuedAt);
      });
    }
  }

  function _receiptMessagesInDom(sessionId: string): HTMLElement[] {
    var container = _chatMessagesEl();
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLElement>('[data-read-receipt-eligible="true"]')).filter(function (node) {
      return node.dataset.sessionId === sessionId && Number.isInteger(Number(node.dataset.msgSeq));
    });
  }

  function _unreadCountMessagesInDom(sessionId: string): HTMLElement[] {
    var container = _chatMessagesEl();
    if (!container) return [];
    return Array.from(container.querySelectorAll<HTMLElement>('[data-unread-count-eligible="true"]')).filter(function (node) {
      return node.dataset.sessionId === sessionId && Number.isInteger(Number(node.dataset.msgSeq));
    });
  }

  function onMessageRendered(messageEl: HTMLElement | null | undefined): void {
    if (_isReceiptEligibleMessage(messageEl)) {
      _observeMessage(messageEl);
      if (_canSendForMessage(messageEl)) _queueForSend(messageEl);
    }
    if (_isUnreadCountEligibleMessage(messageEl)) _applyBufferedCount(messageEl);
  }

  function onMessageReadUpdate(data: unknown): void {
    if (!_isRecord(data) || typeof data.sessionId !== "string" || !Array.isArray(data.counts)) return;
    liveStamp += 1;
    for (var i = 0; i < data.counts.length; i++) {
      var item = data.counts[i];
      var logSeq = _isRecord(item) ? Number(item.logSeq) : Number.NaN;
      if (!Number.isInteger(logSeq) || typeof item.count !== "number") continue;
      _applyCountOrBuffer(data.sessionId, logSeq, item.count, liveStamp);
    }
  }

  function restore(sessionId: string): void {
    if (!sessionId) return;
    _pruneBufferedCounts();
    var receiptMessages = _receiptMessagesInDom(sessionId);
    var countMessages = _unreadCountMessagesInDom(sessionId);
    receiptMessages.forEach(function (messageEl) {
      _observeMessage(messageEl);
    });
    countMessages.forEach(_applyBufferedCount);
    if (countMessages.length) {
      var seqs = countMessages.map(_messageSeq);
      void _fetchRangeAndApply(sessionId, Math.min.apply(Math, seqs), Math.max.apply(Math, seqs));
    }
    receiptMessages.forEach(function (messageEl) {
      if (_canSendForMessage(messageEl)) _queueForSend(messageEl);
    });
  }

  function restoreRange(sessionId: string, fromSeq: number, toSeq: number): void {
    if (!sessionId || !Number.isInteger(fromSeq) || !Number.isInteger(toSeq) || fromSeq > toSeq) return;
    void _fetchRangeAndApply(sessionId, fromSeq, toSeq);
  }

  function reset(): void {
    pending.forEach(function (entry) { _clearPendingTimer(entry); });
    pending.clear();
    delivered.clear();
    visibleKeys.clear();
    readState.clear();
    bufferedCounts.clear();
    _disconnectObserver();
  }

  function onSocketOpen(): void {
    if (typeof window === "undefined" || !window.currentSessionId) return;
    pending.forEach(function (entry) {
      if (entry.sessionId !== window.currentSessionId) return;
      entry.attempts = 0;
      _clearPendingTimer(entry);
    });
    void _flushPending(window.currentSessionId);
  }

  document.addEventListener("visibilitychange", function () {
    if (document.visibilityState !== "visible") return;
    if (typeof window === "undefined" || !window.currentSessionId) return;
    pending.forEach(function (entry) {
      if (entry.sessionId !== window.currentSessionId) return;
      entry.attempts = 0;
      _clearPendingTimer(entry);
    });
    void _flushPending(window.currentSessionId);
  });

  return {
    onMessageRendered: onMessageRendered,
    onMessageReadUpdate: onMessageReadUpdate,
    restore: restore,
    restoreRange: restoreRange,
    reset: reset,
    onSocketOpen: onSocketOpen,
  };
})();
